import React, { useEffect } from 'react';

import { Tab, TabGroup, TabList, TabPanels, TabPanel } from '@headlessui/react';
import TabButton from '@dashboard/components/TabButton';
import CheckoutConfirmationMessageForm from './CheckoutConfirmationMessageForm';
import RedirectAfterPaymentForm from './RedirectAfterPaymentForm';
import CheckoutRedirectForm from './CheckoutRedirectForm';
import { usePreview } from '@dashboard/context/PreviewContext';
import { AfterPaymentActionEnum } from '@dashboard/enums/after-payment';
import CanUseFeature, { useCanUseFeature } from '@dashboard/components/CanUseFeature';
import Features from '@dashboard/enums/features';
import usePage from '@dashboard/hooks/usePage';

function getTabIndex(checkout) {
  if (!checkout.afterPaymentAction) return 0;

  switch (checkout.afterPaymentAction) {
    case AfterPaymentActionEnum.CONFIRMATION:
      return 0;
    case AfterPaymentActionEnum.CHECKOUT:
      return 1;
    case AfterPaymentActionEnum.REDIRECT:
      return 2;
    default:
      return 0;
  }
}

const PageConfirmationForm = () => {
  const { page } = usePage();
  const tabIndex = getTabIndex(page);
  const [, previewDispatch] = usePreview();
  const setPreview = value => previewDispatch({ type: 'set', value });

  const canUseMultiStepCheckout = useCanUseFeature(Features.MULTI_STEP_CHECKOUT);

  const canUseRedirect = useCanUseFeature(Features.REDIRECT_TO_URL);

  useEffect(() => {
    if (page.afterPaymentAction === AfterPaymentActionEnum.CONFIRMATION) {
      setPreview({
        type: 'confirmationCheckoutMessage',
      });
    }

    return () =>
      setPreview({
        type: 'checkout',
      });
  }, []);

  return (
    <TabGroup defaultIndex={tabIndex}>
      <TabList className="flex mt-1 gap-x-2">
        <Tab className="w-1/3 outline-none">
          {({ selected }) => <TabButton selected={selected}>Display message</TabButton>}
        </Tab>

        <Tab className="w-1/3 outline-none" disabled={!canUseMultiStepCheckout}>
          {({ selected }) => (
            <CanUseFeature feature={Features.MULTI_STEP_CHECKOUT} inset>
              <TabButton selected={selected} disabled={!canUseMultiStepCheckout}>
                Next page
              </TabButton>
            </CanUseFeature>
          )}
        </Tab>

        <Tab className="w-1/3 outline-none" disabled={!canUseRedirect}>
          {({ selected }) => (
            <CanUseFeature feature={Features.REDIRECT_TO_URL} inset>
              <TabButton selected={selected} disabled={!canUseRedirect}>
                Redirect to URL
              </TabButton>
            </CanUseFeature>
          )}
        </Tab>
      </TabList>

      <TabPanels className="mt-4">
        <TabPanel>
          <CheckoutConfirmationMessageForm />
        </TabPanel>
        <TabPanel>
          <CheckoutRedirectForm />
        </TabPanel>
        <TabPanel>
          <RedirectAfterPaymentForm />
        </TabPanel>
      </TabPanels>
    </TabGroup>
  );
};

export default PageConfirmationForm;
